import React, { useState, useRef, useEffect } from "react";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css";

/**
 * Reusable ContextMenu component
 * Usage:
 * <ContextMenu options={[{ label: 'Edit', onClick: () => {} }, ...]} />
 * Call showContextMenu(x, y) to display at position
 */
function ContextMenu({ options, visible, position, onClose }) {
    const menuRef = useRef(null);
    const [adjustedPos, setAdjustedPos] = useState(position);

    useEffect(() => {
        if (!visible) return;
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [visible, onClose]);

    // Adjust menu position to prevent overflow
    useEffect(() => {
        if (!visible) return;
        const menu = menuRef.current;
        if (!menu) {
            setAdjustedPos(position);
            return;
        }
        const { innerWidth, innerHeight } = window;
        const rect = menu.getBoundingClientRect();
        let x = position.x;
        let y = position.y;
        // If menu would overflow right, shift left
        if (x + rect.width > innerWidth) {
            x = Math.max(0, innerWidth - rect.width - 4);
        }
        // If menu would overflow bottom, shift up
        if (y + rect.height > innerHeight) {
            y = Math.max(0, innerHeight - rect.height - 4);
        }
        setAdjustedPos({ x, y });
    }, [visible, position]);

    if (!visible) return null;

    return (
        <div
            className="CustomContextMenu"
            ref={menuRef}
            style={{
                position: "fixed",
                top: adjustedPos.y,
                left: adjustedPos.x,
                zIndex: 2000,
                minWidth: 160,
                background: "var(--bb-shell-bg)",
                border: "1px solid var(--bb-accent)",
                borderRadius: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                padding: "6px 0"
            }}
        >
            {options.map((opt, idx) => {
                // Handle separator
                if (opt.separator) {
                    return (
                        <div
                            key={idx}
                            style={{
                                height: '0.5px',
                                background: 'var(--bb-accent)',
                                opacity: 0.5
                            }}
                        />
                    );
                }
                return (
                    <div
                        key={idx}
                        className="CustomContextMenu__item"
                        // Remove onClick from here
                        // Add onMouseDown to handle left/middle click
                        onMouseDown={e => {
                            e.preventDefault();
                            if (opt.url && e.button === 1) {
                                // Middle mouse: open in new tab, keep focus here
                                window.open(opt.url, "_blank", "noopener,noreferrer");
                                window.focus(); // Try to refocus this tab
                                onClose();
                            } else if (e.button === 0) {
                                opt.onClick();
                                onClose();
                            }
                        }}
                        style={{
                            padding: "8px 20px",
                            color: "var(--bb-shell-text)",
                            cursor: "pointer",
                            fontSize: 15,
                            borderBottom: idx !== options.length - 1 ? "1px solid #222" : "none",
                            transition: "background 0.2s"
                        }}
                    >
                        {opt.icon && <span style={{ marginRight: 8 }}>{opt.icon}</span>}
                        {opt.label}
                    </div>
                );
            })}
        </div>
    );
}

// Input types that accept text being pasted/typed into them
const TEXT_INPUT_TYPES = ["text", "search", "url", "tel", "email", "password", "number"];

/**
 * Walks up from the given element and returns the closest editable element
 * (text-like input, textarea, or contenteditable), or null if there isn't one.
 */
function getEditableTarget(target) {
    let element = target;
    while (element && element !== document.body) {
        if (element.tagName === "INPUT") {
            const type = (element.getAttribute("type") || "text").toLowerCase();
            if (TEXT_INPUT_TYPES.includes(type) && !element.disabled && !element.readOnly) {
                return element;
            }
            return null;
        }
        if (element.tagName === "TEXTAREA") {
            return element.disabled || element.readOnly ? null : element;
        }
        if (element.isContentEditable) {
            return element;
        }
        element = element.parentElement;
    }
    return null;
}

/**
 * Copies text to the clipboard, falling back to a hidden textarea +
 * execCommand for non-secure contexts / older browsers.
 */
async function copyTextToClipboard(text) {
    if (!text) return;
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return;
        }
    } catch (err) {
        console.error("Clipboard write failed, falling back:", err);
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    try {
        document.execCommand("copy");
    } catch (err) {
        console.error("Fallback clipboard copy failed:", err);
    }
    document.body.removeChild(textarea);
}

// Sets an input/textarea value through the native setter so React's
// onChange fires for controlled components
function setNativeInputValue(element, value) {
    const prototype = element.tagName === "TEXTAREA"
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    const valueSetter = Object.getOwnPropertyDescriptor(prototype, "value").set;
    valueSetter.call(element, value);
    element.dispatchEvent(new Event("input", { bubbles: true }));
}

/**
 * Reads text from the clipboard and inserts it into the given editable
 * element at the current caret/selection position.
 */
async function pasteIntoEditableTarget(target) {
    if (!target || !navigator.clipboard || !navigator.clipboard.readText) return;
    let text;
    try {
        text = await navigator.clipboard.readText();
    } catch (err) {
        console.error("Clipboard read failed (permission denied?):", err);
        return;
    }
    if (!text) return;
    target.focus();
    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        const value = target.value;
        let start = value.length;
        let end = value.length;
        try {
            // selectionStart/End are null (or throw) on some input types
            if (target.selectionStart !== null) start = target.selectionStart;
            if (target.selectionEnd !== null) end = target.selectionEnd;
        } catch (err) { /* keep end-of-value fallback */ }
        setNativeInputValue(target, value.slice(0, start) + text + value.slice(end));
        try {
            const caret = start + text.length;
            target.setSelectionRange(caret, caret);
        } catch (err) { /* not supported on this input type */ }
    } else if (target.isContentEditable) {
        document.execCommand("insertText", false, text);
    }
}

/**
 * Returns the text currently selected by the user. Falls back to the
 * editable target's own selection range, since window.getSelection()
 * doesn't report selections inside inputs in all browsers.
 */
function getSelectedText(editableTarget) {
    let selectedText = window.getSelection ? window.getSelection().toString() : "";
    if (
        !selectedText &&
        editableTarget &&
        (editableTarget.tagName === "INPUT" || editableTarget.tagName === "TEXTAREA") &&
        editableTarget.type !== "password"
    ) {
        try {
            const { selectionStart, selectionEnd, value } = editableTarget;
            if (selectionStart !== null && selectionEnd !== null && selectionEnd > selectionStart) {
                selectedText = value.substring(selectionStart, selectionEnd);
            }
        } catch (err) { /* selection not readable on this input type */ }
    }
    return selectedText;
}

/**
 * Hook to use context menu in any component
 * Returns: { showContextMenu, hideContextMenu, ContextMenuComponent }
 * Quick links are automatically prepended to the options if available
 *
 * @param {Array|Function} baseOptions - Array of options or a function that returns options based on context
 */
export function useContextMenu(baseOptions = []) {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [contextData, setContextData] = useState(null);
    const [clipboardContext, setClipboardContext] = useState({ selectedText: "", editableTarget: null });
    const { quickLinks } = useQuickLinks();

    const showContextMenu = (x, y, data = null) => {
        // Capture what's under the cursor and any text selection at the moment
        // the menu opens, so Copy/Paste can be offered only where they apply
        const targetAtPoint = document.elementFromPoint(x, y);
        const editableTarget = getEditableTarget(targetAtPoint);
        setClipboardContext({ selectedText: getSelectedText(editableTarget), editableTarget });
        setPosition({ x, y });
        setContextData(data);
        setVisible(true);
    };
    const hideContextMenu = () => setVisible(false);

    // Helper function to check if the target element should use native context menu
    const shouldUseNativeContextMenu = (target) => {
        if (!target) return false;
        
        // Check if the target or any parent is an editable element
        let element = target;
        while (element && element !== document.body) {
            // Check for input elements
            if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
                return true;
            }
            
            // Check for contenteditable elements (like Quill editor)
            if (element.contentEditable === 'true' || element.getAttribute('contenteditable') === 'true') {
                return true;
            }
            
            // Check for elements with specific classes that indicate text editing
            if (element.classList && (
                element.classList.contains('ql-editor') || 
                element.classList.contains('ql-container') ||
                element.classList.contains('quill') ||
                element.hasAttribute('spellcheck')
            )) {
                return true;
            }
            
            // Check for drawing canvas elements - skip global context menu for these
            if (element.classList && (
                element.classList.contains('drawing-canvas') ||
                element.classList.contains('fullscreen-canvas-area') ||
                element.tagName === 'CANVAS'
            )) {
                return true;
            }
            
            element = element.parentElement;
        }
        
        return false;
    };

    // Enhanced context menu handler that respects editable content
    const handleContextMenu = (e) => {
        // Check if we should use native context menu for spell-check
        if (shouldUseNativeContextMenu(e.target)) {
            // Don't prevent default - let native context menu show
            return;
        }
        
        // Show enhanced context menu for non-editable areas
        e.preventDefault();
        showContextMenu(e.clientX, e.clientY);
    };


    // Clipboard utilities — only offered where they actually apply:
    // Copy needs a text selection, Paste needs an editable right-click target
    // (pages that show the custom menu over inputs, e.g. direct onContextMenu bindings)
    const clipboardOptions = [];
    if (clipboardContext.selectedText) {
        clipboardOptions.push({
            label: "Copy",
            icon: <i className="fas fa-copy"></i>,
            onClick: () => copyTextToClipboard(clipboardContext.selectedText)
        });
    }
    if (clipboardContext.editableTarget && navigator.clipboard && navigator.clipboard.readText) {
        clipboardOptions.push({
            label: "Paste",
            icon: <i className="fas fa-paste"></i>,
            onClick: () => pasteIntoEditableTarget(clipboardContext.editableTarget)
        });
    }

    // Always-available options
    const alwaysOptions = [
        { separator: true },
        {
            label: "Copy Page Link",
            icon: <i className="fas fa-link"></i>,
            onClick: () => copyTextToClipboard(window.location.href)
        },
        {
            label: "Enter Full Screen",
            icon: <i className="fas fa-expand"></i>,
            onClick: () => {
                if (document.documentElement.requestFullscreen) {
                    document.documentElement.requestFullscreen();
                } else if (document.documentElement.webkitRequestFullscreen) {
                    document.documentElement.webkitRequestFullscreen();
                } else if (document.documentElement.msRequestFullscreen) {
                    document.documentElement.msRequestFullscreen();
                }
            }
        },
        {
            label: "Refresh Page",
            icon: <i className="fas fa-sync-alt"></i>,
            onClick: () => window.location.reload()
        }
    ];

    // Determine final options - support both static array and dynamic function
    const finalBaseOptions = typeof baseOptions === 'function' ? baseOptions(contextData) : baseOptions;

    // For sidebar-specific menus (when contextData exists), don't add quick links or always options
    // For global menus (when contextData is null), add quick links and always options
    let allOptions;
    
    if (contextData && typeof baseOptions === 'function') {
        // Sidebar-specific context menu - only show sidebar options
        allOptions = finalBaseOptions;
    } else {
        // Global context menu - show all options
        allOptions = [
            ...clipboardOptions,
            ...(clipboardOptions.length > 0 && (quickLinks.length > 0 || finalBaseOptions.length > 0)
                ? [{ separator: true }]
                : []),
            ...quickLinks.map(link => ({
                label: link.name,
                icon: <i className="fas fa-external-link-alt"></i>,
                onClick: () => {
                    window.open(link.url, '_blank', 'noopener,noreferrer');
                }
            })),
            ...(quickLinks.length > 0 && finalBaseOptions.length > 0 ? [{ separator: true }] : []),
            ...finalBaseOptions,
            ...alwaysOptions
        ];
    }

    const ContextMenuComponent = (
        <ContextMenu
            options={allOptions}
            visible={visible}
            position={position}
            onClose={hideContextMenu}
        />
    );

    return { showContextMenu, hideContextMenu, ContextMenuComponent, handleContextMenu };
}

export default ContextMenu;
